import { apiClient } from '@/libs/api_client'
import axios from 'axios'
import { ClinicUser } from '@magnetar/data-access'

interface ClinicUserResponse {
  success: boolean
  data: ClinicUser | null
  error?: {
    code: string
    message: string
  }
}

interface CreateClinicUserRequest {
  clinic_id: number
  user_id: number
}

interface CreateClinicUserByAccountRequest {
  clinic_id: number
  account: string
}

interface CreateNewUserAndAddToClinicRequest {
  clinic_id: number
  account: string
  user_name: string
  email?: string
  user_role_id?: number
}

interface UpdateClinicUserRequest {
  clinic_id: number
  user_id: number
  user_role_id?: number | null
}

export const clinicUsersService = {
  updateClinicUser: async (
    id: number,
    data: UpdateClinicUserRequest,
    token: string,
  ): Promise<ClinicUserResponse> => {
    try {
      const response = await apiClient.put<ClinicUserResponse>(`/api/clinic-users/${id}`, data, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })
      return response.data
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        return error.response.data as ClinicUserResponse
      }
      return {
        success: false,
        data: null,
        error: {
          code: 'UPDATE_CLINIC_USER_FAILED',
          message: '更新診所使用者失敗',
        },
      }
    }
  },

  createClinicUser: async (
    data: CreateClinicUserRequest,
    token: string,
  ): Promise<ClinicUserResponse> => {
    try {
      const response = await apiClient.post<ClinicUserResponse>('/api/clinic-users', data, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })
      return response.data
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        return error.response.data as ClinicUserResponse
      }
      return {
        success: false,
        data: null,
        error: {
          code: 'CREATE_CLINIC_USER_FAILED',
          message: '建立診所使用者失敗',
        },
      }
    }
  },

  deleteClinicUser: async (id: number, token: string): Promise<ClinicUserResponse> => {
    try {
      const response = await apiClient.delete<ClinicUserResponse>(`/api/clinic-users/${id}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })
      return response.data
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        return error.response.data as ClinicUserResponse
      }
      return {
        success: false,
        data: null,
        error: {
          code: 'DELETE_CLINIC_USER_FAILED',
          message: '刪除診所使用者失敗',
        },
      }
    }
  },

  createClinicUserByAccount: async (
    data: CreateClinicUserByAccountRequest,
    token: string,
  ): Promise<ClinicUserResponse> => {
    try {
      const response = await apiClient.post<ClinicUserResponse>(
        '/api/clinic-users/by-account',
        data,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      )
      return response.data
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        return error.response.data as ClinicUserResponse
      }
      return {
        success: false,
        data: null,
        error: {
          code: 'CREATE_CLINIC_USER_FAILED',
          message: '建立診所使用者失敗',
        },
      }
    }
  },

  createNewUserAndAddToClinic: async (
    data: CreateNewUserAndAddToClinicRequest,
    token: string,
  ): Promise<ClinicUserResponse> => {
    try {
      const response = await apiClient.post<ClinicUserResponse>(
        '/api/clinic-users/new-user',
        data,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      )
      return response.data
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        return error.response.data as ClinicUserResponse
      }
      return {
        success: false,
        data: null,
        error: {
          code: 'CREATE_NEW_USER_FAILED',
          message: '建立新使用者失敗',
        },
      }
    }
  },

  // Alias for createClinicUserByAccount - used for adding investors
  addUserToClinicByAccount: async (
    data: CreateClinicUserByAccountRequest,
    token: string,
  ): Promise<ClinicUserResponse> => {
    return clinicUsersService.createClinicUserByAccount(data, token)
  },
}
